import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Card,
  CardActions,
  IconButton,
  LinearProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";

import apiClient, { resolveFileUrl, useApiQuery } from "../api/client";

const AdminVideos = () => {
  const { data, refetch } = useApiQuery("/videos");
  const videos = data || [];

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [type, setType] = useState("update");
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState("");

  const resetForm = () => {
    setTitle("");
    setDate("");
    setType("update");
    setVideoFile(null);
    setThumbFile(null);
    setProgress(0);
  };

  const upload = async () => {
    if (!title || !videoFile) {
      setToast("Title and video file are required.");
      return;
    }
    const formData = new FormData();
    formData.append("title", title);
    formData.append("date", date);
    formData.append("type", type);
    formData.append("video", videoFile);
    if (thumbFile) formData.append("thumb", thumbFile);

    setUploading(true);
    try {
      await apiClient.post("/videos", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setToast("Video uploaded.");
      resetForm();
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/videos/${id}`);
      setToast("Video deleted.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to delete.");
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Videos
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Upload New Video
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Date (e.g. Feb 2026)" value={date} onChange={(e) => setDate(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
              <MenuItem value="update">Update (Video Updates section)</MenuItem>
              <MenuItem value="overview">Overview (3D Walkthrough — single video)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button component="label" startIcon={<UploadRoundedIcon />} variant="outlined" fullWidth>
              {videoFile ? videoFile.name : "Choose Video (mp4/webm)"}
              <input type="file" accept="video/mp4,video/webm" hidden onChange={(e) => setVideoFile(e.target.files[0])} />
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button component="label" startIcon={<UploadRoundedIcon />} variant="outlined" fullWidth>
              {thumbFile ? thumbFile.name : "Choose Thumbnail (optional)"}
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => setThumbFile(e.target.files[0])} />
            </Button>
          </Grid>
        </Grid>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption">{progress}%</Typography>
          </Box>
        )}

        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={upload}
          disabled={uploading}
        >
          {uploading ? "Uploading…" : "Upload Video"}
        </Button>
      </Paper>

      <Grid container spacing={2}>
        {videos.map((v) => (
          <Grid item xs={12} sm={6} md={4} key={v._id}>
            <Card sx={{ p: 2 }}>
              <video
                src={resolveFileUrl(v.videoUrl)}
                controls
                style={{ width: "100%", borderRadius: 4, marginBottom: 8 }}
              />
              <Typography variant="subtitle2" fontWeight={700}>{v.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {v.type} · {v.date}
              </Typography>
              <CardActions sx={{ justifyContent: "flex-end", px: 0 }}>
                <IconButton color="error" onClick={() => remove(v._id)}>
                  <DeleteRoundedIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast("")}>
        <Alert severity="info" onClose={() => setToast("")}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminVideos;
